import React, { useState, useEffect } from "react";
import { Box, TextField, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { Save, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
const DEFAULT_POLICY = `Data Collection Policy

Last updated: ${new Date().getFullYear()}

1. Overview
This Data Collection Policy explains in detail what data we collect, why we collect it, how long we keep it, and the choices you have regarding your data.

2. Data We Collect

a) Information You Provide
- Contact information: name, phone number, and email address when you fill out forms or place orders.
- Order information: items ordered, quantities, delivery preferences, and payment method selections.
- Communications: messages you send us through the contact form or WhatsApp.

b) Information Collected Automatically
- Usage data: pages visited, time spent on pages, and links clicked.
- Device information: browser type, operating system, and screen resolution.
- Cookies and similar tracking technologies (see Section 5).

3. Why We Collect This Data

- Order processing: to receive, confirm, and fulfill your purchases.
- Customer support: to respond to inquiries and resolve issues.
- Service improvement: to understand how visitors use our site and improve the experience.
- Legal compliance: to meet obligations under applicable laws and regulations.

4. Data Retention
We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy or as required by law. Order records are typically kept for a minimum of 5 years for accounting and legal purposes. You may request deletion of your data at any time (see Section 7).

5. Cookies
We use the following types of cookies:
- Essential cookies: required for the website to function (e.g., cart session).
- Analytics cookies: help us understand visitor behavior (e.g., Google Analytics, if enabled).
You can control cookie settings through your browser. Disabling essential cookies may affect website functionality.

6. Third-Party Services
We may share data with trusted third parties solely to operate our services:
- Payment processors: to handle transactions securely.
- Delivery partners: to fulfill shipping when applicable.
- Analytics providers: to measure site performance.
We do not sell your personal data to any third party for marketing purposes.

7. Your Rights
Depending on your jurisdiction, you may have the right to:
- Access the personal data we hold about you.
- Request correction of inaccurate data.
- Request deletion of your data.
- Object to or restrict certain processing activities.
- Withdraw consent at any time where processing is based on consent.
To exercise any of these rights, please contact us through the Contact page.

8. Data Security
We implement industry-standard security measures including encrypted connections (HTTPS), access controls, and regular security reviews to protect your data from unauthorized access or disclosure.

9. Changes to This Policy
We may update this policy from time to time. The date at the top of this page reflects the most recent revision. Continued use of the website after changes constitutes acceptance of the updated policy.

10. Contact Us
For questions or requests regarding this Data Collection Policy, please reach out via the Contact page on our website.`;
const DataCollectionTab = ({
  settings,
  save,
  saving
}) => {
  const {
    t
  } = useTranslation("website");
  const [content, setContent] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings) {
      setContent(settings.dataCollectionContent || DEFAULT_POLICY);
    }
  }, [settings]);
  const handleSave = async () => {
    await save({
      dataCollectionContent: content
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  const handleReset = () => {
    if (window.confirm(t("Reset to the default data collection policy text?"))) {
      setContent(DEFAULT_POLICY);
    }
  };
  return <Box>
      <Typography variant="body2" color="text.secondary" sx={{
      mb: 1
    }}>{t("This content is displayed on your public Data Collection Policy page at")}
        {" "}
        <strong>/w/{settings?.slug}/data-collection</strong>{t(". Edit the text below to match your business practices. Plain text only — line breaks are preserved.")}


      </Typography>

      {saved && <Alert severity="success" sx={{
      mb: 2
    }}>{t("Data collection policy saved successfully.")}

      </Alert>}

      <TextField multiline fullWidth minRows={24} value={content} onChange={e => setContent(e.target.value)} variant="outlined" inputProps={{
      style: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
        lineHeight: 1.7
      }
    }} sx={{
      mb: 2
    }} />
      

      <Box sx={{
      display: "flex",
      gap: 1.5
    }}>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}>{t("Save Data Collection Policy")}


        </Button>
        <Button variant="outlined" color="inherit" onClick={handleReset} startIcon={<RotateCcw size={16} />}>{t("Reset to Default")}


        </Button>
      </Box>
    </Box>;
};
export default DataCollectionTab;
